import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Landmark, Palette, Leaf, Music } from 'lucide-react';

const faqs = [
  {
    q: "What is a heritage resort in Jaisalmer?",
    a: "A heritage resort in Jaisalmer is one that draws on Rajasthan's architectural and cultural traditions — think sandstone construction, carved wooden elements, traditional courtyard layouts, and interiors that evoke the region's artisan crafts rather than generic hotel design. Rudragarh's heritage accommodations are built in this tradition: Cascade Cottages are water-facing cottages with a traditional Jharoka window seat and cascade pond and waterfall views, while The Cocoon is a wood-scented artisan cabin beside the greenhouse."
  },
  {
    q: "What heritage rooms does Rudragarh offer?",
    a: "Rudragarh has two heritage-style rooms. Cascade Cottages (₹12,000/night, up to 2 guests) are private water-facing cottages with a traditional Jharoka window seat and cascade pond and waterfall views. Extra bedding is available on request. The Cocoon (₹4,200/night, up to 2 guests) is a wood-scented artisan cabin beside the greenhouse — warm and private. Both include minibar and access to all resort facilities."
  },
  {
    q: "What traditional cultural experiences are available at Rudragarh?",
    a: "Cultural experiences at Rudragarh include nightly cultural programs with folk music and dance, a traditional low-sitting dining hall with regional Rajasthani food, live and open kitchen where guests can watch meals being prepared, an open-air theatre for evening performances, and a greenhouse that's part of the property's connection to the land. Beyond the built environment, the camel safaris, horse riding, and archery are also deeply rooted in Rajasthan's desert culture."
  }
];

const highlights = [
  { icon: <Landmark size={28} />, title: "Heritage Architecture", desc: "Cascade Cottages are water-facing cottages with a traditional Jharoka window seat and cascade pond and waterfall views that ground you in the landscape." },
  { icon: <Palette size={28} />, title: "Artisan Craftsmanship", desc: "The Cocoon is a wood-scented artisan cabin beside the greenhouse — warm and private, in the craft tradition of the desert." },
  { icon: <Music size={28} />, title: "Cultural Programs", desc: "Evening folk music, dance, and storytelling under the open sky — Rajasthani culture, unscripted and alive." },
  { icon: <Leaf size={28} />, title: "Natural Pond & Waterfall", desc: "A genuine desert water feature on the property — the cascade pond and waterfall views from Cascade Cottages are a defining feature." }
];

const rooms = [
  { slug: "cascade-cottages", name: "Cascade Cottages", price: 12000, guests: 2, category: "heritage", desc: "Private water-facing cottages with a traditional Jharoka window seat and cascade pond and waterfall views. Extra bedding available on request." },
  { slug: "cocoon", name: "The Cocoon", price: 4200, guests: 2, category: "heritage", desc: "A wood-scented artisan cabin beside the greenhouse — warm, private, and quietly characterful." },
  { slug: "heritage-huts", name: "The Heritage Huts", price: 15000, guests: 4, category: "unique", desc: "Two-level eco-chic bamboo huts with an open kitchen and private porch — natural materials, desert air, earthy textures." }
];

const HeritageResort = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Heritage Resort in Jaisalmer | Pond-View Cottages at Rudragarh</title>
        <meta name="description" content="Rudragarh's heritage cottages offer authentic Rajasthani charm — pond views, artisan wooden rooms, and traditional architecture near Sam Sand Dunes, Jaisalmer." />
        <meta property="og:title" content="Heritage Resort in Jaisalmer | Pond-View Cottages at Rudragarh" />
        <meta property="og:description" content="Stay in heritage-style accommodations at Rudragarh — pond-view cottages, artisan wooden rooms, cultural programs, and authentic Rajasthani hospitality near Sam Sand Dunes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/heritage-resort" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-huts-dusk.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Heritage Resort Jaisalmer | Rudragarh Pond-View Cottages" />
        <meta name="twitter:description" content="Authentic Rajasthani heritage stays at Rudragarh — pond-view cottages, artisan wooden rooms, and cultural programs near Sam Sand Dunes." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-huts-dusk.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/heritage-resort" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-huts-dusk.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/75 via-void/55 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Heritage Stays, Jaisalmer</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Heritage Stays with <span className="text-gold-500">Rajasthani Soul</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Pond-view cottages, artisan wooden rooms, and cultural evenings under the desert sky — Rajasthan as it was always meant to feel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">The Tradition</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rajasthani heritage, <span className="text-gold-500">lived in</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Rajasthan is one of the world's great travel destinations precisely because of its layered, living heritage — its architecture, crafts, music, and food traditions that have survived centuries. Too often, "heritage resort" becomes a marketing phrase layered onto a standard hotel with some jharokha windows added. Rudragarh is built differently.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              The Cascade Cottages uses traditional Rajasthani construction methods and sits beside the property's natural pond — a genuine landscape feature, not a decorative fountain. The The Cocoon was built with craftspeople who practice the wood-carving traditions that Rajasthan is celebrated for. Each element is material, not surface.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              In the evenings, the cultural programs — folk music, traditional dance, storytelling — take place under open sky. The low-sitting traditional dining hall serves regional food. The Thar Desert frames everything. Heritage here is not a theme. It's the address.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Heritage Elements</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              What Makes Rudragarh a <span className="text-gold-500">Heritage Resort</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Heritage Accommodations</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms Built on <span className="text-gold-500">Tradition</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* Cultural Strip */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">Culture & Craft</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                Rajasthan's living <span className="text-gold-500">traditions</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                The heritage experience at Rudragarh extends beyond the architecture. Every evening, the property comes alive with folk music and traditional Rajasthani dance performed in the open-air theatre. The low-sitting traditional dining hall serves meals in the regional style — a more communal, grounded way to eat than a standard hotel restaurant.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Guests are also welcome at the live kitchen to watch regional dishes being prepared, and the greenhouse on the property reflects Rudragarh's intention to remain connected to the land even in the middle of the desert. Culture here is not a performance for tourists — it's the rhythm of the place.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { title: "Folk Music & Dance", desc: "Nightly cultural programs" },
                { title: "Traditional Hall", desc: "Low-sitting Rajasthani dining" },
                { title: "Live Kitchen", desc: "Watch regional cooking" },
                { title: "Open-Air Theatre", desc: "Evening performances" },
                { title: "Camel Safari", desc: "Desert culture on camelback" },
                { title: "Horse Riding", desc: "Rajasthani equestrian tradition" }
              ].map((item, i) => (
                <div key={i} className="bg-void-subtle border border-gold-500/10 p-4">
                  <h4 className="font-heading text-sm mb-1">{item.title}</h4>
                  <p className="text-foreground/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              About <span className="text-gold-500">Heritage Stays</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-huts-dusk.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/82" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Book a Heritage Stay</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            Experience <span className="text-gold-500">Rajasthan</span> as It Should Feel
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Reserve a heritage cottage or artisan wooden room at Rudragarh and step into the living tradition of the Thar Desert.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HeritageResort;
